import React from 'react';
import { mapValues, isBoolean, get, keyBy, map, some, has, isNull, isArray } from 'lodash';
import { scrollErrorIntoView } from '../../app-main/components/reduxForms/utilities/scrollErrorIntoView';
import accessTypes from './accessTypes';
import { FormattedMessage } from 'react-intl';

const hasItem = (keys, faqData) => {
  return (
    some(keys, (question) => {
      const answer = faqData[question];

      if (!has(faqData, question)) return false;
      if ('' === answer) return false;
      if (isNull(answer)) return false;
      if (isArray(answer) && 0 === answer.length) return false;

      return true;
    })
  );
};

const accessTypeLabels = (answer) => {
  const keyByAccess = keyBy(accessTypes, 'key');
  return map(answer, (item) => {
    return get(keyByAccess, [item, 'label']);
  });
};

const mapBoolToString = (obj) => {
  return mapValues(obj, (value) => {
    if (isBoolean(value)) {
      if (value) return 'true';
      return 'false';
    }
    return value;
  });
};

const showFormError = (errors) => {
  const errorKey = get(Object.keys(errors), '0');

  if (!errorKey || '_error' === errorKey) {
    return;
  }

  return scrollErrorIntoView(errors);
};

const lightFAQ = (answer) => (
  {
    has_natural_light: {
      label: answer ? 'Space has natural light' : 'Space does not have natural light'
    },
    can_block_out_light: {
      label: answer ? 'Natural light can be blocked out' : 'Natural light can not be blocked out'
    },
    lighting_description: {
      label: 'Lighting description',
      description: answer
    }
  }
);

const soundFAQ = (answer) => (
  {
    has_soundproofing: {
      label: answer ? 'Space is soundproof' : 'Space is not soundproof'
    },
    ambient_noise_description: {
      label: 'Ambient noise description',
      description: answer
    }
  }
);

const accessFAQ = (answer) => (
  {
    space_access_types: {
      label: 'Crew access',
      description: accessTypeLabels(answer).join(', ')
    },
    equipment_loading_description: {
      label: 'Equipment loading description',
      description: answer
    },
    has_separate_crew_area: {
      label: answer ? 'Staging area is available' : 'No staging area available'
    },
    separate_crew_area_description: {
      description: answer
    },
    has_off_limit_areas: {
      label: answer ? 'Some rooms are off-limits for shooting' : 'All rooms are available for shooting'
    },
    off_limit_areas_description: {
      description: answer
    },
    can_film_exterior: {
      label: answer ? 'Shooting the exterior of the building is allowed' : 'Shooting the exterior of the building is not allowed'
    }
  }
);

const electricalFAQ = (answer) => (
  {
    electrical_system_description: {
      label: 'Electrical system description',
      description: answer
    },
    can_access_circuit_breaker: {
      label: answer ? 'Circuit breaker can be accessed' : 'Circuit breaker can not be accessed'
    }
  }
);

const rulesFAQ = (answer) => (
  {
    rules: {
      label: <FormattedMessage id='invite.rules_faq.general_rules' defaultMessage='General Rules' />,
      description: answer
    },
    min_age: {
      label: 0 < answer
        ? <FormattedMessage id='invite.rules_faq.ages_allowed' defaultMessage='Ages {age}+' values={{ age: answer }} />
        : <FormattedMessage id='invite.rules_faq.all_ages_allowed' defaultMessage='All ages are allowed in the space' />
    },
    requires_production_insurance: {
      label: answer
        ? <FormattedMessage id='invite.rules_faq.insurance_is_required' defaultMessage='Production insurance is required' />
        : <FormattedMessage id='invite.rules_faq.insurance_is_not_required' defaultMessage='Production insurance is not required' />
    },
    insurance_requirement_description: {
      description: answer
    },
    can_move_furniture: {
      label: answer
        ? <FormattedMessage id='invite.rules_faq.moved_furniture_decorations' defaultMessage='Furniture and decorations may be moved' />
        : <FormattedMessage id='invite.rules_faq.no_moving_furniture_decorations' defaultMessage='No moving furniture and decorations' />
    },
    furniture_move_description: {
      description: answer
    },
    can_use_smoke_machines: {
      label: answer
        ? <FormattedMessage id='invite.rules_faq.smoke_allowed' defaultMessage='Smoke machines are allowed' />
        : <FormattedMessage id='invite.rules_faq.no_smoke_allowed' defaultMessage='No smoke machines allowed' />
    },
    nudity_allowed: {
      label: answer
        ? <FormattedMessage id='invite.rules_faq.nudity_allowed' defaultMessage='Nudity is allowed' />
        : <FormattedMessage id='invite.rules_faq.no_nudity_allowed' defaultMessage='No nudity allowed' />
    }
  }
);

const productionQuestions = {
  lighting: {
    label: 'Lighting',
    icon: 'lighting',
    keys: [
      'has_natural_light',
      'can_block_out_light',
      'lighting_description'
    ],
    faq: lightFAQ
  },
  sound: {
    label: 'Sound',
    icon: 'sound',
    keys: [
      'has_soundproofing',
      'ambient_noise_description'
    ],
    faq: soundFAQ
  },
  access: {
    label: 'Space access',
    icon: 'access',
    keys: [
      'space_access_types',
      'equipment_loading_description',
      'has_separate_crew_area',
      'separate_crew_area_description',
      'has_off_limit_areas',
      'off_limit_areas_description',
      'can_film_exterior'
    ],
    faq: accessFAQ
  },
  electrical: {
    label: 'Electrical',
    icon: 'electrical',
    keys: [
      'can_access_circuit_breaker',
      'electrical_system_description'
    ],
    faq: electricalFAQ
  },
  rules: {
    label: 'Host rules',
    icon: 'rules',
    keys: [
      'requires_production_insurance',
      'insurance_requirement_description',
      'can_move_furniture',
      'furniture_move_description',
      'can_use_smoke_machines',
      'nudity_allowed'
    ],
    faq: rulesFAQ
  }
};

export { mapBoolToString, showFormError, productionQuestions, hasItem };
